import React, { PureComponent } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import styled from "styled-components";
import classNames from "classnames";

import { withPanelStyle, CFontIcon, Text } from "@appcues/component-library";
import { showModal } from "actions/currentModal";
import { selectAccountMeta } from "reducers/account/meta";
import { selectAccountStats } from "reducers/account/stats";
import { CLICKED_INTEGRATION } from "constants/events";
import {
  CONFIRM_REMOVE_INTEGRATION_MODAL,
  CONFIRM_ACTIVATE_INTEGRATION_MODAL,
  INSTALL_TO_INTEGRATE_MODAL,
  ZAPIER_INTEGRATION_MODAL,
  SALESFORCE_BETA_INTEGRATION_MODAL,
  HUBSPOT_TWO_WAY_MODAL
} from "constants/globals";

import { trackEvent } from "actions/events";

import betaFlag from "assets/images/beta-flag.svg";
import { selectIsInstalled } from "reducers/account/installedDomains";
class IntegrationCard extends PureComponent {
  onClickIntegrationCard = () => {
    const { actions, isEnabled, integration, isInstalled } = this.props;

    if (!isInstalled) {
      actions.showModal(INSTALL_TO_INTEGRATE_MODAL);
      this.trackIntegrationClickEvent(INSTALL_TO_INTEGRATE_MODAL);
    } else if (integration.name === "Zapier") {
      actions.showModal(ZAPIER_INTEGRATION_MODAL, { integration });
      this.trackIntegrationClickEvent(ZAPIER_INTEGRATION_MODAL);
    } else if (integration.name === "Salesforce") {
      actions.showModal(SALESFORCE_BETA_INTEGRATION_MODAL, { integration });
      this.trackIntegrationClickEvent(SALESFORCE_BETA_INTEGRATION_MODAL);
    } else if (integration.id === "hubspot-two-way-integration") {
      actions.showModal(HUBSPOT_TWO_WAY_MODAL, { integration });
      this.trackIntegrationClickEvent(HUBSPOT_TWO_WAY_MODAL);
    } else if (!isEnabled) {
      actions.showModal(CONFIRM_ACTIVATE_INTEGRATION_MODAL, { integration });
      this.trackIntegrationClickEvent(CONFIRM_ACTIVATE_INTEGRATION_MODAL);
    } else {
      actions.showModal(CONFIRM_REMOVE_INTEGRATION_MODAL, { integration });
      this.trackIntegrationClickEvent(CONFIRM_REMOVE_INTEGRATION_MODAL);
    }
  };

  trackIntegrationClickEvent = modal => {
    const { actions, meta, integration, mau, isInstalled } = this.props;
    const isCustomer = !!meta.fusebillSubscriptionId;

    actions.trackEvent(CLICKED_INTEGRATION, {
      isInstalled: isInstalled,
      stripePlanId: meta.stripePlanId,
      stripeSubCreatedAt: meta.stripeSubCreatedAt,
      mau: mau,
      id: meta.id,
      firstEmbedTimestamp: meta.firstEmbedTimestamp,
      isCustomer: isCustomer,
      integration: integration.name,
      modal: modal
    });
  };

  render() {
    const { integration, isEnabled } = this.props;

    return (
      <StyledIntegrationCard
        className={classNames(isEnabled && "isEnabled")}
        onClick={this.onClickIntegrationCard}
      >
        {!integration.hideCardAction && (
          <StyledIntegrationCardAction>
            <CFontIcon
              className={classNames(
                "fas fa-fw",
                isEnabled ? "fa-times" : "fa-plus"
              )}
            />
          </StyledIntegrationCardAction>
        )}
        {integration.beta && (
          <StyledBetaBanner>
            <img alt="beta" src={betaFlag} />
          </StyledBetaBanner>
        )}
        <StyledIntegrationCardImg
          src={`data:image/png;base64, ${integration.base64Image}`}
        />
        {integration.isTwoWay && (
          <CardFooter>
            <CFontIcon className="fas fa-exchange-alt" /> <Text>Two-way</Text>
          </CardFooter>
        )}
      </StyledIntegrationCard>
    );
  }
}

IntegrationCard.propTypes = {
  integration: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    base64Image: PropTypes.string.isRequired,
    isTwoWay: PropTypes.bool
  }).isRequired,
  isEnabled: PropTypes.bool.isRequired
};

IntegrationCard.defaultProps = {
  integration: { id: "", name: "", base64Image: "" },
  isEnabled: false
};

const StyledCardAction = styled.div`
  position: absolute;
  pointer-events: none;
  transition: opacity 0.2s ease;
`;

const CardFooter = styled.div`
  position: absolute;
  bottom: 0;
  padding: 15px;
  left: 0;
`;

const StyledIntegrationCardAction = styled(StyledCardAction)`
  right: 8px;
  top: 8px;
  opacity: 0;
`;

const StyledBetaBanner = styled(StyledCardAction)`
  left: 10px;
  top: 10px;
  opacity: 0.8;

  img {
    max-width: 40px;
  }
`;

export const StyledIntegrationCard = withPanelStyle(styled.div`
  position: relative;
  height: 100px;
  padding: ${props => (props.padding ? props.padding : "20px 40px")};
  border: 2px solid transparent;
  text-align: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  color: ${props => props.theme["$green-dark"]};

  &.isEnabled,
  &:hover {
    border-color: ${props => props.theme["$green"]};
    box-shadow: ${props => props.theme["$box-shadow-2"]};
  }

  &.isEnabled {
    color: ${props => props.theme["$gray-5"]};
  }

  &:hover {
    & ${StyledIntegrationCardAction}, ${StyledBetaBanner} {
      opacity: 1;
      pointer-events: all;
    }

    &.isEnabled {
      border-color: ${props => props.theme["$red"]};
    }
  }
`);

export const StyledIntegrationCardImg = styled.img`
  height: 100%;
`;

const mapStateToProps = state => {
  const meta = selectAccountMeta(state);
  const accountStats = selectAccountStats(state);
  const isInstalled = selectIsInstalled(state);
  const mau =
    accountStats && accountStats.monthlyActiveUsers
      ? accountStats.monthlyActiveUsers
      : 0;

  return {
    meta,
    mau,
    isInstalled
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      showModal,
      trackEvent
    },
    dispatch
  )
});
export default connect(mapStateToProps, mapDispatchToProps)(IntegrationCard);
